// src/pages/InvoicePage.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import {
  UploadCloud,
  Clock,
  History,
  FileText,
  CheckCircle,
  CreditCard,
} from "lucide-react";

export default function InvoicePage() {
  const navigate = useNavigate();
  const {
    isAdmin,
    isDeveloper,
    hasPermission,
  } = useUserPermissions();

  const canUse = (permission) =>
    Boolean(
      isAdmin ||
      isDeveloper ||
      hasPermission(permission)
    );

  const canUpload =
    canUse("invoice.invoice.upload");

  const canApprove =
    canUse("invoice.invoice.approve");

  const canHistory =
    canUse("invoice.invoice.view");

  const canReview =
    canUse("invoice.invoice.view");

  const canPay =
    canUse("invoice.invoice.pay");

  const tiles = [
    canUpload && {
      title: "Upload Invoice",
      description: "Submit a new vendor invoice",
      icon: <UploadCloud className="w-6 h-6 text-green-400" />,
      route: "/invoice/upload",
      bg: "from-green-900 via-green-950 to-black",
    },
    canApprove && {
      title: "Pending Approvals",
      description: "Approve or reject invoices ready for finance review",
      icon: <Clock className="w-6 h-6 text-yellow-400" />,
      route: "/invoice/approval",
      bg: "from-yellow-900 via-yellow-950 to-black",
    },
    canHistory && {
      title: "Invoice History",
      description: "Browse processed invoices",
      icon: <History className="w-6 h-6 text-blue-400" />,
      route: "/invoice/history",
      bg: "from-blue-900 via-blue-950 to-black",
    },
    canReview && {
      title: "Invoice Review",
      description: "Compare uploaded invoices with auto-generated PO invoices",
      icon: <CheckCircle className="w-6 h-6 text-purple-400" />,
      route: "/invoice/review",
      bg: "from-purple-900 via-purple-950 to-black",
    },
    canPay && {
      title: "Pay Invoices",
      description: "Settle approved invoices",
      icon: <CreditCard className="w-6 h-6 text-emerald-400" />,
      route: "/invoice/pay",
      bg: "from-emerald-900 via-emerald-950 to-black",
    },
  ].filter(Boolean);

  return (
    <div className="min-h-screen p-10 bg-gradient-to-br from-black via-gray-900 to-gray-950 text-white">
      <h1 className="text-3xl font-extrabold mb-2">🧾 Invoice Dashboard</h1>

      <p className="text-gray-400 mb-10">
        Access invoice upload, review, approval, history, and payment workflows.
      </p>

      {tiles.length === 0 ? (
        <div className="text-amber-300 text-sm">
          Your account doesn’t currently have invoice permissions.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {tiles.map((tile, idx) => (
            <div
              key={`${tile.title}-${idx}`}
              onClick={() => navigate(tile.route)}
              className={`cursor-pointer rounded-2xl bg-gradient-to-br ${tile.bg} p-6 shadow-lg border border-white/10 hover:shadow-2xl transition-transform hover:scale-105`}
            >
              <div className="flex items-center justify-between mb-4">
                {tile.icon}
                <FileText className="w-5 h-5 text-white/10" />
              </div>

              <h2 className="text-xl font-semibold mb-1">{tile.title}</h2>
              <p className="text-sm text-gray-300">{tile.description}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
